import { useCallback } from 'react';
import { useMutation, useQuery } from '@apollo/client';

import { GET_CALORIE_LOGS } from '../graphql/queries';
import { CREATE_CALORIE_LOG, UPDATE_CALORIE_LOG } from '../graphql/mutations';

/**
 * The calorie tracker's week, shared by the tracker and the diet plan: the plan's day N is this week's day N
 * (Monday first), and a day's log holds the foods eaten, each tagged with the plan meal it belongs to (mealIndex).
 */

export const getTodayIndex = () => (new Date().getDay() + 6) % 7;

// YYYY-MM-DD in local time (toISOString would use UTC and roll the day over late in the evening)
export const toLocalDate = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

/** This week's day as a local Date at midnight (0 = Monday … 6 = Sunday). */
export const getDayDate = (dayIndex) => {
  const today = new Date();
  return new Date(today.getFullYear(), today.getMonth(), today.getDate() - getTodayIndex() + dayIndex);
};

/** The date of this week's day (0 = Monday … 6 = Sunday). */
export const getDateForDayIndex = (dayIndex) => toLocalDate(getDayDate(dayIndex));

/** Whole numbers the way the site's language writes them: 2.217 (pt), 2,217 (en). */
export const numberFormatter = (language) => (value) => Math.round(value || 0).toLocaleString(language === 'pt' ? 'pt-BR' : 'en-US');

export const getCurrentWeekRange = () => ({ dateGte: getDateForDayIndex(0), dateLte: getDateForDayIndex(6) });

export const sumEntries = (entries) =>
  (entries || []).reduce(
    (acc, entry) => ({
      calories: acc.calories + (entry.calories || 0),
      protein: acc.protein + (entry.protein || 0),
      carbs: acc.carbs + (entry.carbs || 0),
      fat: acc.fat + (entry.fat || 0),
    }),
    { calories: 0, protein: 0, carbs: 0, fat: 0 }
  );

/** "80g" → "80 g", "1 scoop (30g)" → "1 scoop (30 g)": the plan's quantities, spaced the way the app writes them. */
export const formatQuantity = (quantity) => String(quantity || '').replace(/(\d)\s?(kg|g|ml)\b/g, '$1 $2');

/** The day's goal counts as hit from 95% to 110% of the target. */
export const isGoalHit = (calories, target) => !!target && calories >= target * 0.95 && calories <= target * 1.1;

/**
 * Take the Nth food of a meal out of the day's entries (entries keep every meal's foods in one list).
 * Returns the remaining entries, the food taken out and where it was, so an undo can put it back in place.
 */
export const removeMealEntry = (entries, mealIndex, mealEntryIndex) => {
  let count = -1;
  const at = (entries || []).findIndex((entry) => (entry.mealIndex ?? 0) === mealIndex && ++count === mealEntryIndex);
  if (at === -1) return null;
  return { entries: [...entries.slice(0, at), ...entries.slice(at + 1)], removed: entries[at], at };
};

/** Put a removed food back where it was (or at the end, if the day changed since). */
export const restoreEntry = (entries, entry, at) => {
  const list = entries || [];
  const index = Math.min(at, list.length);
  return [...list.slice(0, index), entry, ...list.slice(index)];
};

/** A planned food as a tracker entry, under its meal. */
export const planFoodToEntry = (food, mealIndex) => ({
  name: food.name,
  quantity: food.quantity,
  calories: Math.round(food.calories || 0),
  protein: Math.round(food.protein || 0),
  carbs: Math.round(food.carbs || 0),
  fat: Math.round(food.fat || 0),
  mealIndex,
});

/** This week's logs, and adding foods to a day (creating its log on the first one). */
export const useWeekCalorieLog = ({ skip = false } = {}) => {
  const { data, loading, refetch } = useQuery(GET_CALORIE_LOGS, { variables: getCurrentWeekRange(), skip });
  const [createLog, { loading: isCreating }] = useMutation(CREATE_CALORIE_LOG);
  const [updateLog, { loading: isUpdating }] = useMutation(UPDATE_CALORIE_LOG);
  const logs = data?.calorieLogs?.data;

  const getLogForDate = useCallback((dateStr) => (logs || []).find((log) => log.attributes.date === dateStr) || null, [logs]);

  const addEntries = useCallback(
    async (dateStr, newEntries) => {
      const existing = getLogForDate(dateStr);
      if (existing) {
        const entries = [...(existing.attributes.entries || []), ...newEntries];
        await updateLog({ variables: { id: existing.id, data: { entries, totalCalories: sumEntries(entries).calories } } });
      } else {
        await createLog({ variables: { data: { date: dateStr, entries: newEntries, totalCalories: sumEntries(newEntries).calories } } });
      }
      await refetch();
    },
    [getLogForDate, createLog, updateLog, refetch]
  );

  return { logs, loading, refetch, getLogForDate, addEntries, isSaving: isCreating || isUpdating };
};
